import { useEffect, useState, useRef, useCallback } from "react";
import { CheckCircle2, ShoppingBag } from "lucide-react";
import { useCart } from "../context/AppContext";
import { useSubscribe } from "./marketing/useSubscribe";
import { ModalShell, PromoCodeBox, EmailCaptureForm } from "./marketing/ModalParts";

// Fire once per session — regardless of whether the welcome modal was already shown.
const SEEN_KEY = "bl_abandon_seen";
const MIN_CART_DWELL_MS = 12000; // require at least this much cart-having time before firing

// Exit-intent (mouse leaves top) after the cart has been non-empty for MIN_CART_DWELL_MS.
function useAbandonTrigger(cartCount, onFire) {
  const cartStartedAt = useRef(null);

  useEffect(() => {
    if (cartCount > 0 && !cartStartedAt.current) cartStartedAt.current = Date.now();
    else if (cartCount === 0) cartStartedAt.current = null;
  }, [cartCount]);

  useEffect(() => {
    if (sessionStorage.getItem(SEEN_KEY) || cartCount === 0) return;
    const handleExit = (e) => {
      if (e.clientY > 0 || !cartStartedAt.current) return;
      if (Date.now() - cartStartedAt.current < MIN_CART_DWELL_MS) return;
      if (sessionStorage.getItem(SEEN_KEY)) return;
      sessionStorage.setItem(SEEN_KEY, "1");
      onFire();
    };
    document.addEventListener("mouseout", handleExit);
    return () => document.removeEventListener("mouseout", handleExit);
  }, [cartCount, onFire]);
}

function CartPreview({ items, subtotal }) {
  const itemCount = items.reduce((s, x) => s + x.quantity, 0);
  return (
    <div className="mt-6 border border-white/8 bg-white/[0.02] p-4">
      <div className="flex items-baseline justify-between mb-3">
        <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-white/40">
          {itemCount} item{itemCount === 1 ? "" : "s"} · A${subtotal.toFixed(2)}
        </div>
        <div className="bl-eyebrow text-[color:var(--bl-gold)]">Saved for you</div>
      </div>
      <div className="space-y-1 max-h-24 overflow-y-auto">
        {items.slice(0, 3).map((i) => (
          <div key={`${i.product_id}-${i.variant_id}`} className="text-white/70 text-xs flex justify-between">
            <span className="truncate">{i.name} · {i.strength}</span>
            <span className="font-mono text-white/40 ml-2 shrink-0">×{i.quantity}</span>
          </div>
        ))}
        {items.length > 3 && <div className="text-white/40 text-xs">+ {items.length - 3} more</div>}
      </div>
    </div>
  );
}

export default function AbandonCartModal() {
  const { items, subtotal, open: cartOpen, setOpen: openCart } = useCart();
  const [open, setOpen] = useState(false);
  const { email, setEmail, submitting, result, submit, copyCode } = useSubscribe("abandon_cart", "Couldn't send — try again");

  const show = useCallback(() => {
    if (cartOpen) openCart(false);
    setOpen(true);
  }, [cartOpen, openCart]);
  useAbandonTrigger(items.length, show);

  const close = () => setOpen(false);
  const finishAndOpenCart = () => {
    setOpen(false);
    setTimeout(() => openCart(true), 200);
  };

  if (!open) return null;

  return (
    <ModalShell testid="abandon-modal" zIndex="z-[110]" borderClass="border-[color:var(--bl-gold)]/30" onClose={close} closeTestid="abandon-close">
      {!result ? (
        <>
          <div className="inline-flex items-center gap-2 text-[color:var(--bl-gold)]">
            <ShoppingBag size={14} />
            <span className="font-mono text-[10px] tracking-[0.3em] uppercase">Wait</span>
          </div>
          <h2 className="font-display text-white text-4xl md:text-5xl mt-3 leading-none tracking-tight uppercase">
            Your Cart
            <br /><span className="text-[color:var(--bl-gold)]">Is Almost Full</span>
          </h2>
          <CartPreview items={items} subtotal={subtotal} />
          <p className="text-white/60 text-sm mt-6 leading-relaxed max-w-md">
            Drop your email and we'll <span className="text-[color:var(--bl-gold)]">save 5% off this order</span> plus give you first look at every restock.
          </p>
          <EmailCaptureForm
            email={email} setEmail={setEmail} submitting={submitting} onSubmit={submit} onSkip={close}
            prefix="abandon" submitLabel="Save My Cart · 5% Off" skipLabel="Continue without discount" buttonClass="gold"
          />
        </>
      ) : (
        <div data-testid="abandon-success">
          <CheckCircle2 size={40} className="text-[color:var(--bl-blue)]" />
          <div className="bl-eyebrow text-[color:var(--bl-gold)] mt-6">Saved</div>
          <h2 className="font-display text-white text-4xl mt-3 uppercase">Ready When You Are</h2>
          <PromoCodeBox result={result} onCopy={copyCode} testid="abandon-code-box" caption="· paste at checkout" />
          <p className="text-white/55 text-sm mt-6 leading-relaxed">
            Your cart is still saved. Head to checkout when you're ready.
          </p>
          <button onClick={finishAndOpenCart} data-testid="abandon-checkout" className="mt-6 w-full bl-btn primary py-3">
            Open My Cart
          </button>
        </div>
      )}
    </ModalShell>
  );
}
